import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import scrollToFirstInvalidField from '../functions/scrollToFirstInvalidField';
import getFirstInvalidInput from '../functions/getFirstInvalidInput';
import AnimatedExpandingContainer from './AnimatedExpandingContainer';


function AccordionBase({
    isExpanded: controlledIsExpanded,
    onClick,
    isInitiallyExpanded,

    HeaderComponent,
    headerProps,
    children,

    alwaysRenderChildMarkup,

    name,
    disabled,
    onAnimationEnd,

    shouldIncludeDivider,

    ...otherProps
}) {
    const isControlled = onClick && controlledIsExpanded != null;
    const [ uncontrolledIsExpanded, setUncontrolledExpanded ] = useState(isInitiallyExpanded);
    const containerRef = useRef(null);

    const shouldRenderAsExpanded = isControlled
        ? controlledIsExpanded
        : uncontrolledIsExpanded;

    function handleToggleIsExpanded(e) {
        if (disabled) {
            return;
        }

        onClick?.(e);

        // If controlled, we don't want to do anything when they click
        if (isControlled) {
            return;
        }

        // Only prevent closing, don't prevent opening
        if (uncontrolledIsExpanded && getFirstInvalidInput(containerRef.current)) {
            return scrollToFirstInvalidField(containerRef.current);
        } else {
            setUncontrolledExpanded(!uncontrolledIsExpanded);
        }
    }

    return (
        <Container
            ref={containerRef}
            {...otherProps}
        >
            <HeaderComponent
                isExpanded={shouldRenderAsExpanded}
                onClick={handleToggleIsExpanded}
                onKeyPress={handleToggleIsExpanded}
                aria-controls={name ? `${name}-panel` : undefined}
                disabled={disabled}
                {...headerProps}
            />

            <AnimatedExpandingContainer
                id={name ? `${name}-panel` : undefined}
                isVisible={shouldRenderAsExpanded}
                aria-hidden={!shouldRenderAsExpanded}
                alwaysRenderChildMarkup={alwaysRenderChildMarkup}
                isInitiallyExpanded={isInitiallyExpanded}
                disabled={disabled}
                onEnd={() => onAnimationEnd?.(shouldRenderAsExpanded)}
            >
                {children}
            </AnimatedExpandingContainer>

            <If condition={shouldIncludeDivider}>
                <hr className="rh-horizontal-rule" />
            </If>
        </Container>
    );
}


AccordionBase.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
    isInitiallyExpanded: PropTypes.bool,

    HeaderComponent: PropTypes.elementType.isRequired,
    headerProps: PropTypes.object,
    children: PropTypes.node.isRequired,

    alwaysRenderChildMarkup: PropTypes.bool,
    name: PropTypes.string,
    disabled: PropTypes.bool,
    onAnimationEnd: PropTypes.func,

    shouldIncludeDivider: PropTypes.bool,
};

AccordionBase.defaultProps = {
    isExpanded: undefined,
    onClick: undefined,
    isInitiallyExpanded: false,

    headerProps: {},

    alwaysRenderChildMarkup: false,
    name: undefined,
    disabled: false,
    onAnimationEnd: undefined,

    shouldIncludeDivider: undefined,
};

// Still need this for ref and otherProps
const Container = styled.div``;

export default observer(AccordionBase);
